'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ChatState } from './ChatLayout';

interface QueueOverlayProps {
  chatState: ChatState;
  geoEnabled: boolean;
  partnerInfo?: {
    name?: string;
    gender?: string;
    location?: string;
  };
}

const FALLBACK_JOKES = [
  "My ex had an accident. I told paramedics the wrong blood type. She'll finally experience rejection. 💀",
  "I asked my dog what 2 minus 2 is. He said nothing. 🐶",
  "Why don't scientists trust atoms? Because they make up everything. ☢️",
];

const EMOJIS = ['🍆', '🍑', '💦', '😩', '🔞', '🥵', '😈', '🫦', '👅', '🍒', '😏', '🔥', '💋', '😮', '🤤', '😳'];

export default function QueueOverlay({ chatState, geoEnabled, partnerInfo }: QueueOverlayProps) {
  const [dots, setDots] = useState('');
  const [joke, setJoke] = useState<string>('');
  const [jokeVisible, setJokeVisible] = useState(true);
  const [emojiIndex, setEmojiIndex] = useState(0);
  // Store partnerInfo in ref so it persists even after chatState changes
  const partnerRef = useRef(partnerInfo);
  useEffect(() => {
    if (partnerInfo?.name) partnerRef.current = partnerInfo;
  }, [partnerInfo]);

  const fetchJoke = useCallback(async () => {
    try {
      const res = await fetch(
        'https://v2.jokeapi.dev/joke/Dark,Miscellaneous?blacklistFlags=racist,sexist&type=single'
      );
      const data = await res.json();
      if (!data.error && data.joke) {
        setJokeVisible(false);
        setTimeout(() => {
          setJoke(data.joke);
          setJokeVisible(true);
        }, 400);
      }
    } catch {
      setJoke(FALLBACK_JOKES[Math.floor(Math.random() * FALLBACK_JOKES.length)]);
    }
  }, []);

  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '' : d + '.'));
    }, 500);
    return () => clearInterval(dotsInterval);
  }, []);

  useEffect(() => {
    if (chatState !== 'searching') return;
    const interval = setInterval(() => {
      setEmojiIndex((i) => (i + 1) % EMOJIS.length);
    }, 800);
    return () => clearInterval(interval);
  }, [chatState]);

  useEffect(() => {
    if (chatState !== 'searching') return;
    fetchJoke();
    const interval = setInterval(fetchJoke, 6000);
    return () => clearInterval(interval);
  }, [chatState, fetchJoke]);

  const partner = partnerRef.current;
  const genderColor = partner?.gender === 'male' ? 'text-blue-400' : partner?.gender === 'female' ? 'text-pink-400' : 'text-white';

  // chatting state: hide overlay completely — let video show
  if (chatState === 'chatting') return null;

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10 backdrop-blur-sm">
      <div className="text-center px-4 md:px-6 max-w-sm md:max-w-md w-full">

        {chatState === 'idle' ? (
          <>
            <div className="text-6xl mb-6">✨</div>
            <h3 className="text-white text-xl md:text-2xl font-bold mb-2">Ready to Chat?</h3>
            <p className="text-white/40 text-xs md:text-sm mb-4 md:mb-6">Connect with a random stranger instantly</p>
            <div className="w-12 h-0.5 bg-white/10 mx-auto mb-6 rounded-full" />
            <p className="text-white/50 text-sm">Press <span className="text-white font-semibold underline underline-offset-2">Start Chat</span> to begin</p>
          </>

        ) : chatState === 'searching' ? (
          <>
            <div className="text-4xl md:text-6xl mb-3 md:mb-6 select-none" style={{ transition: 'opacity 0.3s' }}>
              {EMOJIS[emojiIndex]}
            </div>
            <h3 className="text-white text-lg md:text-2xl font-bold mb-1">
              Finding a stranger{dots}
            </h3>
            <p className="text-white/40 text-xs md:text-sm mb-4 md:mb-8">
              {geoEnabled ? 'Searching within 5km' : 'Searching globally'}
            </p>
            <div style={{ opacity: jokeVisible ? 1 : 0, transition: 'opacity 0.4s ease', minHeight: '60px' }}>
              <p className="text-white/60 text-xs md:text-base leading-relaxed italic line-clamp-3 md:line-clamp-none">
                "{joke}"
              </p>
            </div>
          </>

        ) : chatState === 'connecting' && partner?.name ? (
          <>
            <div className="text-4xl md:text-5xl mb-3 md:mb-4">
              {partner.gender === 'male' ? '♂' : partner.gender === 'female' ? '♀' : '👤'}
            </div>
            <p className="text-white/40 text-xs uppercase tracking-widest mb-2 md:mb-3">Match Found!</p>
            <h3 className={`text-3xl md:text-4xl font-bold mb-2 ${genderColor}`}>{partner.name}</h3>
            {partner.location && (
              <p className="text-white/40 text-sm mb-4">📍 {partner.location}</p>
            )}
            <p className="text-white/40 text-sm mb-1">
              {partner.gender === 'male' ? 'Male' : partner.gender === 'female' ? 'Female' : 'Unknown'}
            </p>
            <div className="flex items-center gap-2 justify-center mt-4">
              <div className="w-2 h-2 rounded-full bg-accent pulse-dot" />
              <span className="text-accent text-sm">Connecting{dots}</span>
            </div>
          </>

        ) : (
          <>
            <div className="text-4xl md:text-5xl mb-3 md:mb-4">🤝</div>
            <h3 className="text-white text-lg md:text-xl font-bold mb-2">Match found!</h3>
            <p className="text-white/40 text-sm">Establishing connection{dots}</p>
            <div className="mt-4 flex items-center gap-2 justify-center">
              <div className="w-2 h-2 rounded-full bg-accent pulse-dot" />
              <span className="text-accent text-sm">Connecting{dots}</span>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
